import axios from 'axios';
import { processApiResponse, handleApiError } from './api-utils';
import API_CONFIG, { API_BASE_URL } from '../config';

// Get auth context - this will be set by the app when user is authenticated
let authContext = null;

export function setAuthContext(auth) {
  authContext = auth;
}

/**
 * Enhanced API client with standardized response handling and authentication
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Add request interceptor to include JWT token
apiClient.interceptors.request.use(
  (config) => {
    // Add JWT token to authorization header if available
    if (authContext && authContext.isAuthenticated && authContext.user) {
      // Use ID token for backend authentication (JWT format)
      const token = authContext.user.id_token || authContext.user.access_token;
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle standardized response format
apiClient.interceptors.response.use(
  (response) => processApiResponse(response),
  (error) => {
    // Handle authentication errors
    if (error.response && error.response.status === 401 && authContext) {
      // Token expired or invalid, redirect to login
      console.warn('Authentication failed, redirecting to login');
      authContext.signinRedirect();
    }
    return Promise.reject(handleApiError(error));
  }
);

/**
 * API methods for interacting with the backend
 */
export const api = {
  /**
   * Get all images with optional filtering
   * @param {Object} options - Query options
   * @returns {Promise} - API response
   */
  getImages: (options = {}) => {
    return apiClient.get(API_CONFIG.ENDPOINTS.IMAGES, { params: options });
  },
  
  /**
   * Get a specific image by ID
   * @param {string} id - Image ID
   * @returns {Promise} - API response
   */
  getImageById: (id) => {
    const url = API_CONFIG.ENDPOINTS.IMAGE_BY_ID.replace('{id}', id);
    return apiClient.get(url);
  },

  /**
   * Update an existing image
   * @param {string} id - Image ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise} - API response
   */
  updateImage: (id, updates) => {
    return apiClient.put(`${API_CONFIG.ENDPOINTS.IMAGES}/${id}`, updates);
  },
  
  /**
   * Upload and analyze a single image
   * @param {Object} imageData - Image data and metadata
   * @returns {Promise} - API response
   */
  uploadImage: (imageData) => {
    return apiClient.post(API_CONFIG.ENDPOINTS.UPLOAD, imageData);
  },
  
  /**
   * Upload and analyze multiple images (async processing)
   * @param {Object} batchData - Batch of images and options
   * @returns {Promise} - API response with job ID
   */
  uploadBatch: (batchData) => {
    return apiClient.post(API_CONFIG.ENDPOINTS.BATCH, batchData);
  },

  /**
   * Get presigned URLs for direct S3 upload
   * @param {Object} filesData - { files: [{ filename, filetype }] }
   * @returns {Promise} - API response with presigned URLs
   */
  generateUploadUrls: (filesData) => {
    return apiClient.post('/generate-upload-urls', filesData);
  },

  /**
   * Get batch job status
   * @param {string} jobId - Batch job ID
   * @returns {Promise} - API response with job status
   */
  getBatchJobStatus: (jobId) => {
    return apiClient.get(`${API_CONFIG.ENDPOINTS.BATCH}/${jobId}/status`);
  },
  
  /**
   * Get all sponsors
   * @returns {Promise} - API response
   */
  getSponsors: () => {
    return apiClient.get(API_CONFIG.ENDPOINTS.SPONSORS);
  },
  
  /**
   * Get a specific sponsor by ID
   * @param {string} id - Sponsor ID
   * @returns {Promise} - API response
   */
  getSponsorById: (id) => {
    return apiClient.get(`${API_CONFIG.ENDPOINTS.SPONSORS}/${id}`);
  },
  
  /**
   * Create a new sponsor
   * @param {Object} sponsorData - Sponsor data
   * @returns {Promise} - API response
   */
  createSponsor: (sponsorData) => {
    return apiClient.post(API_CONFIG.ENDPOINTS.SPONSORS, sponsorData);
  },
  
  /**
   * Delete a sponsor
   * @param {string} id - Sponsor ID
   * @returns {Promise} - API response
   */
  deleteSponsor: (id) => {
    return apiClient.delete(`${API_CONFIG.ENDPOINTS.SPONSORS}/${id}`);
  },
  
  /**
   * Get all matches
   * @param {Object} options - Query options
   * @returns {Promise} - API response
   */
  getMatches: (options = {}) => {
    return apiClient.get(API_CONFIG.ENDPOINTS.MATCHES, { params: options });
  },
  
  /**
   * Get a specific match by ID
   * @param {string} id - Match ID
   * @returns {Promise} - API response
   */
  getMatchById: (id) => {
    return apiClient.get(`${API_CONFIG.ENDPOINTS.MATCHES}/${id}`);
  },
  
  /**
   * Create a new match
   * @param {Object} matchData - Match data
   * @returns {Promise} - API response
   */
  createMatch: (matchData) => {
    return apiClient.post(API_CONFIG.ENDPOINTS.MATCHES, matchData);
  },
  
  /**
   * Update a match
   * @param {string} id - Match ID
   * @param {Object} matchData - Match data
   * @returns {Promise} - API response
   */
  updateMatch: (id, matchData) => {
    return apiClient.put(`${API_CONFIG.ENDPOINTS.MATCHES}/${id}`, matchData);
  },
  
  /**
   * Get all images for a match
   * @param {string} matchId - Match ID
   * @returns {Promise} - API response
   */
  getMatchImages: (matchId) => {
    return apiClient.get(`${API_CONFIG.ENDPOINTS.MATCHES}/${matchId}/images`);
  },
  
  /**
   * Associate images with a match
   * @param {string} matchId - Match ID
   * @param {Array} imageIds - Array of image IDs
   * @returns {Promise} - API response
   */
  associateImagesWithMatch: (matchId, imageIds) => {
    return apiClient.post(`${API_CONFIG.ENDPOINTS.MATCHES}/${matchId}/images`, { imageIds });
  },
  
  /**
   * Get all players
   * @param {Object} options - Query options
   * @returns {Promise} - API response
   */
  getPlayers: (options = {}) => {
    // If teamFilter is 'all', remove it from the params
    const params = { ...options };
    if (params.teamFilter === 'all') {
      delete params.teamFilter;
    }
    return apiClient.get(API_CONFIG.ENDPOINTS.PLAYERS, { params });
  },
  
  /**
   * Get a specific player by ID
   * @param {string} id - Player ID
   * @returns {Promise} - API response
   */
  getPlayerById: (id) => {
    return apiClient.get(`${API_CONFIG.ENDPOINTS.PLAYERS}/${id}`);
  },
  
  /**
   * Create a new player
   * @param {Object} playerData - Player data
   * @returns {Promise} - API response
   */
  createPlayer: (playerData) => {
    return apiClient.post(API_CONFIG.ENDPOINTS.PLAYERS, playerData);
  },
  
  /**
   * Update a player
   * @param {string} id - Player ID
   * @param {Object} playerData - Player data
   * @returns {Promise} - API response
   */
  updatePlayer: (id, playerData) => {
    return apiClient.put(`${API_CONFIG.ENDPOINTS.PLAYERS}/${id}`, playerData);
  },
  
  /**
   * Delete a player
   * @param {string} id - Player ID
   * @returns {Promise} - API response
   */
  deletePlayer: (id) => {
    return apiClient.delete(`${API_CONFIG.ENDPOINTS.PLAYERS}/${id}`);
  },
  
  /**
   * Get all players for a match
   * @param {string} matchId - Match ID
   * @returns {Promise} - API response
   */
  getMatchPlayers: (matchId) => {
    return apiClient.get(`${API_CONFIG.ENDPOINTS.MATCHES}/${matchId}/players`);
  },
  
  /**
   * Associate a player with a match
   * @param {string} matchId - Match ID
   * @param {string} playerId - Player ID
   * @param {Object} metadata - Additional metadata
   * @returns {Promise} - API response
   */
  associatePlayerWithMatch: (matchId, playerId, metadata = {}) => {
    return apiClient.post(`${API_CONFIG.ENDPOINTS.MATCHES}/${matchId}/players/${playerId}`, metadata);
  },

  /**
   * Get current user information
   * @returns {Promise} - API response
   */
  getCurrentUser: () => {
    return apiClient.get('/me');
  },

  /**
   * Get all users (Admin only)
   * @param {Object} options - Query options
   * @returns {Promise} - API response
   */
  getUsers: (options = {}) => {
    return apiClient.get('/users', { params: options });
  },

  /**
   * Get all groups (Admin only)
   * @returns {Promise} - API response
   */
  getGroups: () => {
    return apiClient.get('/groups');
  },

  /**
   * Add user to group (Admin only)
   * @param {string} username - Username
   * @param {string} groupName - Group name
   * @returns {Promise} - API response
   */
  addUserToGroup: (username, groupName) => {
    return apiClient.post(`/users/${username}/groups/${groupName}`);
  },

  /**
   * Remove user from group (Admin only)
   * @param {string} username - Username
   * @param {string} groupName - Group name
   * @returns {Promise} - API response
   */
  removeUserFromGroup: (username, groupName) => {
    return apiClient.delete(`/users/${username}/groups/${groupName}`);
  }
};

export default apiClient;
